// pages/ScanMedicalPage.jsx
import React, { useState, useRef } from 'react';
import { Camera, Upload, FileText, X, CheckCircle } from 'lucide-react';

const ScanMedicalPage = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);

  const pageStyles = {
    gap: '1.5rem'
  };

  const cardStyles = {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    border: '1px solid #e5e7eb',
    padding: '1.5rem'
  };

  const headingStyles = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '1.5rem'
  };

  const uploadAreaStyles = {
    border: '2px dashed #d1d5db',
    borderRadius: '8px',
    padding: '2rem',
    textAlign: 'center'
  };

  const primaryButtonStyles = {
    backgroundColor: '#4f46e5',
    color: '#ffffff',
    padding: '0.5rem 1rem',
    borderRadius: '8px',
    border: 'none',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '0.875rem'
  };

  const secondaryButtonStyles = {
    backgroundColor: '#ffffff',
    color: '#374151',
    padding: '0.5rem 1rem',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '0.875rem'
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setScanResult(null);
    }
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setStream(mediaStream);
      setCameraActive(true);
    } catch (err) {
      console.error('Error accessing camera:', err);
      alert('Unable to access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCameraActive(false);
  };

  const captureImage = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);
      
      canvas.toBlob((blob) => {
        const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });
        setSelectedFile(file);
        stopCamera();
      }, 'image/jpeg');
    }
  };

  const scanDocument = () => {
    if (!selectedFile) {
      alert('Please select or capture an image first');
      return;
    }

    setIsScanning(true);
    setScanResult(null);

    // Simulate API call for scanning
    setTimeout(() => {
      const mockResults = {
        documentType: 'Medical Prescription',
        confidence: 92,
        extractedData: {
          patientName: 'John Smith',
          date: '2024-01-15',
          medication: 'Amoxicillin 500mg',
          dosage: 'Take one tablet every 8 hours',
          doctor: 'Dr. Sarah Johnson',
          notes: 'Complete full course even if symptoms improve'
        },
        recommendations: [
          'Store in a cool, dry place',
          'Take with food to avoid stomach upset',
          'Avoid alcohol during treatment'
        ]
      };
      
      setScanResult(mockResults);
      setIsScanning(false);
    }, 2000);
  };

  const clearSelection = () => {
    setSelectedFile(null);
    setScanResult(null);
    if (cameraActive) {
      stopCamera();
    }
  };

  return (
    <div style={pageStyles}>
      <div style={headingStyles}>
        <FileText style={{ marginRight: '0.75rem' }} size={24} color="#7B61FF" />
        <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#111827' }}>
          Scan Medical Document
        </h1>
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr',
        gap: '2rem'
      }}>
        {/* Left Column - Upload and Camera */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* File Upload Section */}
          <div style={cardStyles}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
              Upload Document
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={uploadAreaStyles}>
                {selectedFile ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                      <FileText size={48} style={{ color: '#6366f1' }} />
                      <div style={{ textAlign: 'left' }}>
                        <p style={{ fontWeight: '500' }}>{selectedFile.name}</p>
                        <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          {(selectedFile.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={clearSelection}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '0.5rem 1rem',
                        border: '1px solid #fca5a5',
                        color: '#b91c1c',
                        borderRadius: '6px',
                        backgroundColor: 'transparent',
                        cursor: 'pointer'
                      }}
                    >
                      <X size={16} style={{ marginRight: '0.5rem' }} />
                      Remove File
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload style={{ margin: '0 auto', height: '3rem', width: '3rem', color: '#9ca3af' }} />
                    <div style={{ marginTop: '1rem' }}>
                      <label style={{ cursor: 'pointer' }}>
                        <span style={{ 
                          display: 'block', 
                          marginTop: '0.5rem', 
                          fontSize: '0.875rem', 
                          fontWeight: '500', 
                          color: '#111827' 
                        }}>
                          Choose a file or drag and drop
                        </span>
                        <span style={{ 
                          display: 'block', 
                          marginTop: '0.25rem', 
                          fontSize: '0.75rem', 
                          color: '#6b7280' 
                        }}>
                          PNG, JPG, PDF up to 10MB
                        </span>
                        <input
                          type="file"
                          style={{ display: 'none' }}
                          accept="image/*,.pdf"
                          onChange={handleFileUpload}
                        />
                        <button style={{
                          ...primaryButtonStyles,
                          marginTop: '1rem'
                        }}>
                          Select File
                        </button>
                      </label>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Camera Section */}
          <div style={cardStyles}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
              Use Camera
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {cameraActive ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      style={{ width: '100%', height: '16rem', objectFit: 'cover' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                      onClick={captureImage}
                      style={{
                        flex: 1,
                        display: 'inline-flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '0.5rem 1rem',
                        backgroundColor: '#059669',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Capture
                    </button>
                    <button
                      onClick={stopCamera}
                      style={{
                        flex: 1,
                        display: 'inline-flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '0.5rem 1rem',
                        border: '1px solid #d1d5db',
                        color: '#374151',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        backgroundColor: '#ffffff'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={startCamera}
                  style={{
                    width: '100%',
                    display: 'inline-flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    border: '2px dashed #d1d5db',
                    borderRadius: '8px',
                    backgroundColor: 'transparent',
                    cursor: 'pointer'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = '#818cf8';
                    e.currentTarget.style.backgroundColor ='#e0e7ff';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <Camera style={{ marginRight: '0.75rem' }} size={24} />
                  Open Camera
                </button>
              )}
            </div>
          </div>

          {/* Scan Button */}
          <button
            onClick={scanDocument}
            disabled={!selectedFile || isScanning}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              fontWeight: '500',
              border: 'none',
              cursor: !selectedFile || isScanning ? 'not-allowed' : 'pointer',
              backgroundColor: !selectedFile || isScanning ? '#d1d5db' : '#4f46e5',
              color: !selectedFile || isScanning ? '#6b7280' : '#ffffff',
              fontSize: '1rem'
            }}
          >
            {isScanning ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{
                  animation: 'spin 1s linear infinite',
                  marginRight: '0.75rem',
                  height: '1.25rem',
                  width: '1.25rem',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: '#ffffff',
                  borderRadius: '50%'
                }} />
                Scanning...
              </span>
            ) : (
              'Scan Document'
            )}
          </button>
        </div>

        {/* Right Column - Results */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div style={cardStyles}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
              Scan Results
            </h2>
            {scanResult ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div style={{
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: '8px',
                  padding: '1rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircle style={{ color: '#22c55e', marginRight: '0.75rem' }} size={24} />
                    <div>
                      <h3 style={{ fontWeight: '600', color: '#166534' }}>
                        {scanResult.documentType} Detected
                      </h3>
                      <p style={{ color: '#16a34a', fontSize: '0.875rem' }}>
                        Confidence: {scanResult.confidence}%
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 style={{ fontWeight: '500', color: '#374151', marginBottom: '0.75rem' }}>
                    Extracted Information
                  </h3>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '1rem'
                  }}>
                    {Object.entries(scanResult.extractedData).map(([key, value]) => (
                      <div key={key} style={{
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px',
                        padding: '0.75rem'
                      }}>
                        <p style={{ 
                          fontSize: '0.75rem', 
                          color: '#6b7280', 
                          letterSpacing: '0.05em'
                        }}>
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </p>
                        <p style={{ fontWeight: '500' }}>{value}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {scanResult.recommendations && (
                  <div>
                    <h3 style={{ fontWeight: '500', color: '#374151', marginBottom: '0.75rem' }}>
                      Recommendations
                    </h3>
                    <ul style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {scanResult.recommendations.map((rec, index) => (
                        <li key={index} style={{ display: 'flex', alignItems: 'flex-start' }}>
                          <div style={{
                            flexShrink: 0,
                            height: '1.25rem',
                            width: '1.25rem',
                            borderRadius: '50%',
                            backgroundColor: '#e0e7ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '0.75rem',
                            marginTop: '0.125rem'
                          }}>
                            <div style={{
                              height: '0.5rem',
                              width: '0.5rem',
                              borderRadius: '50%',
                              backgroundColor: '#4f46e5'
                            }} />
                          </div>
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                <FileText style={{ margin: '0 auto', height: '3rem', width: '3rem', color: '#9ca3af' }} />
                <p style={{ marginTop: '1rem', color: '#6b7280' }}>
                  {selectedFile
                    ? 'Click "Scan Document" to analyze your file'
                    : 'Upload or capture a document to see results here'}
                </p>
              </div>
            )}
          </div>

          {/* History Section */}
          <div style={cardStyles}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
              Recent Scans
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {[1, 2, 3].map((item) => (
                <div key={item} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  borderRadius: '8px'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{
                      height: '2.5rem',
                      width: '2.5rem',
                      borderRadius: '8px',
                      backgroundColor: '#e0e7ff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '0.75rem'
                    }}>
                      <FileText size={20} style={{ color: '#4f46e5' }} />
                    </div>
                    <div>
                      <p style={{ fontWeight: '500' }}>Prescription #{item}</p>
                      <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        2 hours ago • 94% confidence
                      </p>
                    </div>
                  </div>
                  <button style={{
                    color: '#4f46e5',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer'
                  }}>
                    View
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScanMedicalPage;